import React, {Fragment} from 'react';
import {useNavigate} from 'react-router-dom';

import {Box, Button, Card, Stack, Typography, capitalize, useTheme} from '@mui/material';
import {OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AlertInfo,
	CTAButtonFixed,
	CopySection,
	MainFooter,
	MainHeader,
	StyledGrid,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {MyAppointmentsEnum} from 'presentation/constants';
import {usePrint} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {PrintTemplate} from 'presentation/modules/myAppointments/ui/components/PrintTemplate';
import {SaveToCalendar} from 'presentation/pages/private/home/components';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {
	formatText,
	getAddressforMaps,
	getAllMonths,
	getCityAndUfByRegex,
	getUfName,
} from 'presentation/utils';
import {AppointmentInstructions, ConfirmationCard} from '../../components';
import {SquareLabelTime} from '../../components/SquareLabelTime';
import {FlowEnum, TELEMEDICINE_INFO} from '../../constants';

import {WhoIndicatesTagParametricEnum} from 'domain/usecases/private/home/who-indicates';
import {WhoIndicatesCard} from 'presentation/components/WhoIndicatesCard';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';
import {useCalendarEvent} from 'presentation/modules/booking/shared/hooks/useCalendarEvent';
import {openGoogleMaps} from 'presentation/modules/booking/shared/utils/openGoogleMaps';
import {QualityCertificationBanner} from 'presentation/modules/booking/telehealth/ui/components/QualityCertificationBanner';
import {ProtocolCard} from '../../components/ProtocolCard';
import {StyledBox} from './styles';
import {ConfirmationBookingContentProps} from './types';

const {at, attendanceUnit, attendance, phones, download, professionalAndDate, referAndEarn} =
	translations['pt-br'].commons;
const {openMap} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {confirmationBooking, seeMyAppointments, appointmentSummary} =
	translations['pt-br'].privateFlow.bookingFlow;

const {appointmentCity} = translations['pt-br'].privateFlow.profile;

export function ConfirmationBookingContent({
	flowType,
	bookedAppointment,
}: ConfirmationBookingContentProps) {
	const {isMobile, isTablet, betweenMobileAndDesktop, isLaptop, isDesktop} = useMediaQueries();
	const navigate = useNavigate();
	const {colors, palette} = useTheme();
	const {isPrinting, printContent, printRef} = usePrint();
	const {iconName, alertTitle} = useBookingTheme({flowType});
	const calendarEvent = useCalendarEvent(bookedAppointment);

	const goToMyAppointments = () => {
		navigate(MyAppointmentsEnum.MY_APPOINTMENTS);
	};

	const {
		clinicName,
		formattedAddress,
		formattedDateWithWeekday,
		hour,
		date,
		phoneNumbers,
		providerName,
		specialtyName,
		protocolNumber,
	} = bookedAppointment;

	const [day, month] = date.split(' ')[0].split('/');
	const months = getAllMonths();
	const monthName = months[Number(month) - 1].slug;

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;

	const address = getAddressforMaps(formattedAddress);

	const {district, city, uf} = getCityAndUfByRegex(formattedAddress);

	const handleOpenGoogleMaps = () => openGoogleMaps(address.addressForMaps || '');

	const showAddress = () => {
		if (isOdonto) {
			return {
				first: formatText(district),
				second: `${city}/${getUfName(uf)}`,
			};
		}
		return {
			first: formatText(city),
			second: `${getUfName(uf)}`,
		};
	};

	return (
		<Fragment>
			<MainHeader
				title={confirmationBooking}
				handleBack={goToMyAppointments}
				handleClose={goToMyAppointments}
				variant="subRoute">
				<Card sx={{borderRadius: 0, boxShadow: 'none', border: 0}}>
					<AlertInfo.Root severity="success" sx={{borderRadius: 0, border: 0}}>
						<AlertInfo.Title title={alertTitle} sx={{mb: 0}} />
					</AlertInfo.Root>
				</Card>
			</MainHeader>

			<StyledGrid.Root p={isMobile ? 2 : 4}>
				<StyledGrid.Item sm={12} md={12} lg={8}>
					<Stack gap={2}>
						<Typography variant="subtitle2" color={colors.light.text.secondary}>
							{appointmentSummary}
						</Typography>
						<ConfirmationCard.Root>
							<ConfirmationCard.Header subheader={professionalAndDate}>
								<Stack flexDirection={'row'} gap={2} alignItems={'start'}>
									<SquareLabelTime day={day} month={monthName} />
									<Stack width={'100%'} gap={'2px'}>
										<Typography variant="subtitle2" fontSize={'1.125rem'}>
											{`${capitalize(formattedDateWithWeekday)} ${at} ${hour}`}
										</Typography>
										<ConfirmationCard.Content
											iconName="medical-services-filled"
											primaryText={providerName}
											disableMargin
										/>
										<ConfirmationCard.Content
											iconName={iconName}
											primaryText={specialtyName}
											disableMargin
										/>
									</Stack>
								</Stack>
								<Stack
									sx={{
										width: '100%',
										mt: isMobile ? 1 : 0,
										flexDirection: isMobile ? 'column' : 'row',
										alignItems: 'center',
										justifyContent: 'end',
										gap: '8px',
									}}
									className="remove-me">
									<Button
										disabled={isPrinting}
										variant="outlined"
										endIcon={
											isPrinting ? (
												<Loading size={24} color="inherit" />
											) : (
												<Icon
													name="download"
													color={palette.primary.main}
													size={24}
												/>
											)
										}
										fullWidth={isMobile}
										onClick={printContent}
										data-tag-id={
											OdontoBookingTaggingEnum.CLICK_BAIXAR_AGENDAMENTO
										}>
										{download}
									</Button>
									<SaveToCalendar
										dataTagId={OdontoBookingTaggingEnum.CLICK_SALVAR_NA_AGENDA}
										calendarEvent={calendarEvent}
									/>
								</Stack>
							</ConfirmationCard.Header>
						</ConfirmationCard.Root>
						<Box
							display="grid"
							gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'}
							gap={2}>
							<ConfirmationCard.Root>
								<ConfirmationCard.Header
									subheader={isTelehealth ? attendance : attendanceUnit}
								/>
								<Stack
									flexDirection={'column'}
									alignItems={'start'}
									justifyContent="space-between"
									gap={1}>
									<ConfirmationCard.Content
										iconName={
											isTelehealth
												? 'remember-me-filled'
												: 'location-on-filled'
										}
										primaryText={
											isTelehealth ? TELEMEDICINE_INFO.name : clinicName
										}
										secondaryText={isTelehealth ? '' : formattedAddress}
									/>
									{!isTelehealth ? (
										<Stack
											sx={{
												width: '100%',
												mt: isMobile ? 1 : 0,
												flexDirection: isMobile ? 'column' : 'row',
												alignItems: 'center',
												justifyContent: 'end',
												gap: '8px',
											}}
											className="remove-me">
											<Button
												sx={{minWidth: 'fit-content'}}
												onClick={handleOpenGoogleMaps}
												fullWidth={isTablet}
												variant="outlined"
												endIcon={<Icon name="map" />}>
												{openMap}
											</Button>
										</Stack>
									) : null}
								</Stack>
							</ConfirmationCard.Root>
							{isTelehealth ? (
								<ProtocolCard protocolNumber={protocolNumber} />
							) : (
								<ConfirmationCard.Root>
									<ConfirmationCard.Header subheader={appointmentCity} />
									<ConfirmationCard.Content
										iconName={'my-location-filled'}
										primaryText={showAddress().first}
										secondaryText={showAddress().second}
									/>
								</ConfirmationCard.Root>
							)}
						</Box>
						{!isTelehealth ? <ProtocolCard protocolNumber={protocolNumber} /> : null}
						{phoneNumbers && phoneNumbers.length !== 0 ? (
							<ConfirmationCard.Root>
								<ConfirmationCard.Header subheader={phones} />
								<Stack direction={isTablet ? 'column' : 'row'} spacing={1}>
									{phoneNumbers.map((phoneNumber, index) => (
										<CopySection
											key={`${index}-${phoneNumber}`}
											value={phoneNumber}
										/>
									))}
								</Stack>
							</ConfirmationCard.Root>
						) : null}

						{isMobile ? (
							<CTAButtonFixed.Root>
								<CTAButtonFixed.Action
									onClick={goToMyAppointments}
									data-tag-id={
										OdontoBookingTaggingEnum.CLICK_VER_MEUS_AGENDAMENTOS
									}>
									{seeMyAppointments}
								</CTAButtonFixed.Action>
							</CTAButtonFixed.Root>
						) : (
							<Box display={isLaptop ? 'none' : 'block'}>
								<Stack alignItems="flex-end">
									<Button
										onClick={goToMyAppointments}
										data-tag-id={
											OdontoBookingTaggingEnum.CLICK_VER_MEUS_AGENDAMENTOS
										}
										size="large">
										{seeMyAppointments}
									</Button>
								</Stack>
							</Box>
						)}
					</Stack>
				</StyledGrid.Item>

				<StyledGrid.Item sm={12} md={12} lg={4}>
					<AppointmentInstructions appointment={bookedAppointment} />
					<Stack gap={2} mt={2}>
						<Typography
							fontWeight={600}
							variant="body2"
							color={colors.light.text.secondary}>
							{referAndEarn}
						</Typography>
						<WhoIndicatesCard
							dataTagId={OdontoBookingTaggingEnum.CLICK_SAIBA_MAIS_QUEM_INDICA}
							$showAsBanner={!isMobile && !isDesktop}
							pathParametric={WhoIndicatesTagParametricEnum.BOOKING}
						/>
					</Stack>

					{betweenMobileAndDesktop ? (
						<Box mt={2}>
							<Stack py={1} alignItems="flex-end">
								<Button
									onClick={goToMyAppointments}
									data-tag-id={
										OdontoBookingTaggingEnum.CLICK_VER_MEUS_AGENDAMENTOS
									}
									size="large">
									{seeMyAppointments}
								</Button>
							</Stack>
						</Box>
					) : null}
				</StyledGrid.Item>
			</StyledGrid.Root>

			<StyledBox>
				{isTelehealth ? <QualityCertificationBanner /> : null}
				<MainFooter />
			</StyledBox>

			<PrintTemplate
				bookedAppointment={bookedAppointment}
				flowType={flowType}
				printRef={printRef}
			/>
		</Fragment>
	);
}
